import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { ToggleButton, ToggleButtonGroup, Select, MenuItem, InputLabel, FormControl, Grid, Box, Typography, Link, IconButton, Modal, Button, CircularProgress, ListSubheader } from '@mui/material';
import { styled } from "@mui/material/styles";
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import QuestionBankCard from '../../components/questionBank/Card';
import { useQuestionBank } from './hooks';
import { NoQuestionBank } from '../../components/questionBank/NoQuestionBank';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewIcon from '@mui/icons-material/GridView';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllWithoutPage } from '../../store/course/courseBaseApi';
import Loader from '../../components/Loader';
import { QuestionPagination } from '../../components/questionBank/Pagination';
import Preview from '../course/lessonPlan/assessments/PreviewQuestion'
import { CaretDown, Eye, X } from '@phosphor-icons/react';
import { useCourse } from '../course/hooks';
import { course as courseConst } from '../course/constants';


const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    padding: theme.spacing(1),
    height: '30px',
    border: 'none',
    '&.Mui-selected': {
        background: 'transparent',
        '& .MuiSvgIcon-root': {
            color: 'var(--mui-palette-primary-orange)',
        },
    },
}));


export const QuestionBank = () => {
    const { questionsList, loadQuestions, loadCourseQuestions, courseQuestions, loadLessonPlan, lessonPlan, loading, remove } = useQuestionBank();
    const { loadCoursesList, courseList, diagnosticList, resetCoursesList, lastPage } = useCourse();
    const [questionListDetails, setQuestionListDetails] = useState([])
    const theme = useTheme();
    const [view, setView] = useState('card');
    const navigate = useNavigate();
    const [filterDetails, setFilterDetails] = useState({});
    const [category, setCategory] = useState();
    const [sectionList, setSectionList] = useState();
    const [chapterList, setChapterList] = useState();

    const [openPreview, setOpenPreview] = useState(false);
    const [previewData, setPreviewData] = useState();

    const [page, setPage] = useState(0);
    const [listLoading, setListLoading] = useState(false);


    useEffect(() => {
        loadQuestions();
    }, []);

    useEffect(() => {
        if (filterDetails?.chapterId && filterDetails?.sectionId) {
            loadCourseQuestions({ courseId: filterDetails?.courseId, sectionId: filterDetails?.sectionId, chapterId: filterDetails?.chapterId })
        } else if (filterDetails?.courseId && filterDetails?.sectionId) {
            loadCourseQuestions({ courseId: filterDetails?.courseId, sectionId: filterDetails?.sectionId })
        } else if (filterDetails?.courseId) {
            loadCourseQuestions({ courseId: filterDetails?.courseId })
        } else {
            loadQuestions();
        }
    }, [filterDetails])

    useEffect(() => {
        if (category) {
            loadCoursesList({ pageNo: page, category: category });
            setListLoading(false);
        }
    }, [category, page])

    useEffect(() => {
        if (filterDetails?.courseId) {
            loadLessonPlan({ id: filterDetails?.courseId });
        }
    }, [filterDetails?.courseId]);
    useEffect(() => {
        if (filterDetails?.courseId) {
            setSectionList(lessonPlan?.sections)
        }
    }, [lessonPlan]);
    useEffect(() => {

        if (filterDetails?.chapterId && filterDetails?.sectionId || filterDetails?.courseId && filterDetails?.sectionId || filterDetails?.courseId) {
            if (courseQuestions?.data) {
                const flattenedRows = courseQuestions.data.map(row => ({
                    ...row,
                    text: row.questionDetails?.text
                }));
                setQuestionListDetails(flattenedRows);
            } else {
                setQuestionListDetails([])
            }
        } else {
            if (questionsList.data) {
                const flattenedRows = questionsList.data.map(row => ({
                    ...row,
                    text: row.questionDetails?.text
                }));
                setQuestionListDetails(flattenedRows);
            } else {
                setQuestionListDetails([])
            }
        }
    }, [questionsList, courseQuestions, filterDetails]);

    const handleCategoryChange = (e) => {
        setFilterDetails({})
        setCategory(e.target.value);
        setPage(0);
    }

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setView(newView);
        }
    };

    const onDelete = (question) => {
        if (!confirm('Are you sure to delete this question?')) { return; }
        remove({ id: question, questionBank: true });
    }

    const handlePreview = (question) => {
        setOpenPreview(!openPreview);
        setPreviewData(question)
    }

    const handleClose = () => {
        setOpenPreview(!openPreview);
        setPreviewData({})
    }

    const columns = [
        { field: 'text', headerName: 'Question', flex: 1 },
        { field: 'type', headerName: 'Type', flex: 1 },
        {
            field: 'answerDetails', headerName: 'Mark', flex: 1, renderCell: (params) => {
                return params?.value?.options?.marks
            }
        },
        { field: 'taxonomy', headerName: 'Taxonomy', flex: 1 },
        { field: 'category', headerName: 'Category', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const question = params.row;
                const editLink = `/question-bank/edit/${question.id}`;
                return (
                    <Box>
                           <Link style={styles.actionBtnItem} component={RouterLink} to={editLink} title="Edit Question"><CreateIcon style={{ color: 'var(--mui-palette-primary-blue)', fontSize: '1.2rem' }} /></Link>
                        <Link style={styles.actionBtnItem} onClick={() => handlePreview(question)} title="Preview Question"><Eye style={{ color: 'var(--mui-palette-success-main)', fontSize: '1.2rem' }} /></Link>
                        <Link style={styles.actionBtnItem} onClick={() => onDelete(question?.id)} title="Delete Question">
                            <DeleteIcon style={{ color: 'var(--mui-palette-error-dark)', fontSize: '1.2rem' }} />
                        </Link>
                    </Box>
                )
            }
        }
    ];

    const handleSectionChange = (e) => {
        const section = e.target.value
        setFilterDetails({ ...filterDetails, sectionId: section })
        const selectedSection = lessonPlan?.sections?.find((sec) => sec.sectionId === section);
        setChapterList(selectedSection?.chapters)
    }

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFilterDetails(prevQuestion => ({
            ...prevQuestion,
            [name]: value,
        }));
    }

    // const getFilteredQuestions = () => {
    //     const filteredQuestions = questionListDetails?.filter(question => {
    //         return (
    //             (!filterDetails?.courseId || question.courseId === filterDetails?.courseId) &&
    //             (!filterDetails?.sectionId || question.sectionId === filterDetails?.sectionId) &&
    //             (!filterDetails?.chapterId || question.chapterId === filterDetails?.chapterId)
    //         );
    //     });
    //     const startIndex = (currentPage - 1) * pageSize;
    //     return filteredQuestions.slice(startIndex, startIndex + pageSize);
    // };

    return <>
        <div>
            {loading ? <Loader /> : <>
                <div className='course'>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}>
                        <Typography
                            align="left"
                            variant="h1"
                            color="primary.blue"
                            sx={{
                                margin: '0',
                                fontWeight: '700',
                                lineHeight: '1.5',
                                fontSize: '1.8rem',
                            }}>Question Bank</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                [theme.breakpoints.down('sm')]: {
                                    flexDirection: 'row',
                                },
                            }}>
                            <ToggleButtonGroup style={styles.toggleButtonContainer}
                                sx={{
                                    [theme.breakpoints.down('sm')]: {
                                        justifyContent: 'flex-start',
                                    },
                                }}
                                value={view} exclusive onChange={handleViewChange} aria-label="course view">
                                <StyledToggleButton value="list" aria-label="list view" title="List View"
                                    sx={{
                                        padding: '5px',
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: '0.7rem'
                                        },
                                    }}><TableRowsIcon /></StyledToggleButton>
                                <StyledToggleButton value="card" aria-label="card view" title="Card View"
                                    sx={{
                                        padding: '5px',
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: '0.7rem'
                                        },
                                    }}><GridViewIcon /></StyledToggleButton>
                            </ToggleButtonGroup>
                            <div style={styles.create}>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => navigate('/question-bank/create')}
                                    title='Create Question'
                                    sx={{
                                        background: 'tranaparent',
                                        color: 'var(--mui-palette-primary-button)',
                                        p: 0,
                                    }}
                                >
                                    <AddCircleIcon sx={{ fontSize: '1.8rem', marginTop: '4px' }} />
                                </Link>
                            </div>
                        </Box>
                    </Box>
                </div>

                <Modal open={openPreview} >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            height: '80%',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            borderRadius: '10px',
                            p: 4,
                            overflow: 'auto',
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                            }}
                        >
                            <X />
                        </IconButton>
                        <Preview question={previewData} />
                    </Box>
                </Modal>

                <Box sx={{
                    clear: 'both',
                    width: '100%',
                    float: 'left',
                    pt: 3,
                    pb: 3,
                    position: 'relative',
                    boxSizing: 'border-box',
                }}>
                    {questionListDetails?.length > 0 &&
                        <Grid sx={styles.gridContainer} container spacing={2} mb={3} alignItems="center">
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <InputLabel style={styles.inputLabel}>Learning Category</InputLabel>
                                <FormControl fullWidth>
                                    <Select
                                        name="category"
                                        value={category || ''}
                                        onChange={handleCategoryChange}
                                        placeholder="Course"
                                        sx={{ height: '47px' }}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {Object.keys(courseConst.CATEGORY).map((key, i) => <MenuItem key={i} value={key}>{courseConst.CATEGORY[key]}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {category && <>
                                {category === "Diagnostic" &&
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Diagnostic</InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="courseId"
                                                value={filterDetails?.courseId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Course"
                                                sx={{ height: '47px' }}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {diagnosticList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.id}>{item?.courseName}</MenuItem>
                                                })}
                                                {!lastPage.Diagnostic && (
                                                    <ListSubheader
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <Button sx={{
                                                            width: '100%',
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: 'space-between'
                                                        }} onClick={() => {
                                                            setListLoading(true);
                                                            setTimeout(() => setPage(prev => prev + 1), 0);
                                                        }}>
                                                            {listLoading ? <CircularProgress /> :
                                                                <> Load More <CaretDown /></>}
                                                        </Button>
                                                    </ListSubheader>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                }
                                {category === "Course" && <>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Course</InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="courseId"
                                                value={filterDetails?.courseId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Course"
                                                sx={{ height: '47px' }}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {courseList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.id}>{item?.courseName}</MenuItem>
                                                })}
                                                {!lastPage.Course && (
                                                    <ListSubheader
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <Button sx={{
                                                            width: '100%',
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: 'space-between'
                                                        }} onClick={() => {
                                                            setListLoading(true);
                                                            setTimeout(() => setPage(prev => prev + 1), 0);
                                                        }}>
                                                            {listLoading ? <CircularProgress /> :
                                                                <> Load More <CaretDown /></>}
                                                        </Button>
                                                    </ListSubheader>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Chapter</InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="sectionId"
                                                value={filterDetails?.sectionId || ''}
                                                onChange={(e) => handleSectionChange(e)}
                                                placeholder="Section"
                                                sx={{ height: '47px' }}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {sectionList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.sectionId}>{item?.name}</MenuItem>
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        <InputLabel style={styles.inputLabel}>Session</InputLabel>
                                        <FormControl fullWidth>
                                            <Select
                                                name="chapterId"
                                                value={filterDetails?.chapterId || ''}
                                                onChange={(e) => handleChange(e)}
                                                placeholder="Chapter"
                                                sx={{ height: '47px' }}
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {chapterList?.map((item, index) => {
                                                    return <MenuItem key={index} value={item?.chapterId}>{item?.name}</MenuItem>
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>}
                            </>}
                        </Grid>
                    }
                    {view === 'card' ? (
                        <Grid container sx={{ position: 'relative', zIndex: 1, marginLeft: '-8px', marginRight: '-8px', width: 'auto' }}>
                            {questionListDetails?.length > 0 ? questionListDetails?.map(question => (
                                <Grid item key={question.id} xs={12} sm={6} md={4} lg={3} sx={{ p: 1 }}>
                                    <QuestionBankCard question={question} />
                                </Grid>
                            )) : <NoQuestionBank />}
                        </Grid>
                    ) : (
                        <div style={{ minHeight: 400, width: '100%', overflow: 'auto' }}>
                            {questionListDetails?.length > 0 ?
                                <DataGrid
                                    rows={questionListDetails}
                                    columns={columns}
                                    sx={{
                                        backgroundColor: 'var(--mui-palette-common-white)',
                                        [theme.breakpoints.down('sm')]: {
                                            width: '900px',
                                        },
                                        '& .css-yrdy0g-MuiDataGrid-columnHeaderRow': {
                                            background: 'var(--mui-palette-primary-blue) !important',
                                            color: 'var(--mui-palette-action-active)',
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            background: 'var(--mui-palette-primary-blue) !important',
                                            color: 'var(--mui-palette-common-white)',
                                            '&:focus': {
                                                outline: 'none',
                                            }
                                        },
                                        '& .MuiDataGrid-columnHeaderTitle': {
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: 'var(--mui-palette-common-white)',
                                        },
                                    }}
                                    pageSize={5} />
                                : <NoQuestionBank />}
                        </div>
                    )}
                    {questionListDetails?.length > 0 && <QuestionPagination filterDetails={filterDetails} />}
                </Box>
            </>}
        </div>
    </>
}
const styles = {
    toggleButtonContainer: {
        display: 'flex',
        justifyContent: 'center'
    },
    create: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '0',
        marginBottom: '0',
        marginLeft: '5px',
    },
    courses: {
        marginTop: '2rem'
    },
    label: {
        color: 'var(--mui-palette-common-black)',
        fontWeight: 'bold'
    },
    inputLabel: {
        color: 'var(--mui-palette-primary-darkGrey)',
        marginBottom: '5px',
        fontWeight: '600',
        fontSize: '14px'

    },
    gridContainer: {
        // mt: 2,
    },
    suggestionBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '10px',
    },
    suggestionItem: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '2px',
        cursor: 'pointer',
        boxShadow: '0 4px 8px var(--mui-palette-secondary-boxShadow1)',
        backgroundColor: 'var(--mui-palette-gray-lightGrey300)',
    },
    uploadBtn: {
        width: '50px',
        height: '50px',
        borderRadius: '5px',
    },
    hiddenInput: {
        display: 'none',
    },
    actionBtnItem: {
        marginLeft: '5px',
        marginRight: '5px',
        cursor: 'pointer'
    }
};
